
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import StructuredData from '../../components/seo/StructuredData';

export default function Home() {
  const { t } = useTranslation('common');

  useEffect(() => {
    // Update page title and meta description
    document.title = 'HK PicklePlay - Hong Kong Pickleball Community Platform | Courts, Sessions & Players';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Join Hong Kong\'s premier pickleball community. Discover courts, book sessions, connect with players, and improve your game with HK PicklePlay - the complete platform for pickleball enthusiasts in Hong Kong.');
    }

    // Update canonical URL
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', window.location.origin + '/');
    }
  }, []);

  const features = [
    {
      icon: 'ri-map-pin-line',
      title: t('find_courts', 'Find Courts'),
      description: t('find_courts_desc', 'Discover pickleball courts across Hong Kong with detailed information, amenities, and booking options.'),
      link: '/courts'
    },
    {
      icon: 'ri-calendar-line',
      title: t('join_sessions', 'Join Sessions'),
      description: t('join_sessions_desc', 'Book and participate in organized pickleball sessions with players of all skill levels.'),
      link: '/sessions'
    },
    {
      icon: 'ri-book-open-line',
      title: t('learn_improve', 'Learn & Improve'),
      description: t('learn_improve_desc', 'Access tutorials, guides, and tips to enhance your pickleball skills and knowledge.'),
      link: '/learning'
    },
    {
      icon: 'ri-trophy-line',
      title: t('track_scores', 'Track Scores'),
      description: t('track_scores_desc', 'Use our interactive scoreboard to keep track of matches and save your game history.'),
      link: '/scoreboard'
    }
  ];

  const stats = [
    { number: '50+', label: t('active_courts', 'Active Courts') },
    { number: '1,200+', label: t('community_members', 'Community Members') },
    { number: '300+', label: t('weekly_sessions', 'Weekly Sessions') },
    { number: '15+', label: t('districts_covered', 'Districts Covered') }
  ];

  const faqs = [
    {
      question: "What is pickleball?",
      answer: "Pickleball is a paddle sport that combines elements of tennis, badminton, and ping-pong. It's played on a court with a net, using paddles and a plastic ball with holes."
    },
    {
      question: "How do I book a court in Hong Kong?",
      answer: "You can browse available courts on our platform, check their availability, and book directly through our booking system. Many courts offer online booking for convenience."
    },
    {
      question: "What equipment do I need to start playing?",
      answer: "You'll need a pickleball paddle, pickleballs, and appropriate athletic shoes. Many courts provide equipment for beginners or rent paddles on-site."
    },
    {
      question: "Are there beginner-friendly sessions available?",
      answer: "Yes! We have many sessions specifically designed for beginners, including instructional sessions and mixed-skill level games where experienced players help newcomers."
    }
  ];

  return (
    <>
      <StructuredData type="WebSite" data={{}} />
      <StructuredData type="Organization" data={{}} />
      <StructuredData type="FAQPage" data={{ faqs }} />
      
      <div className="min-h-screen">
        {/* Hero Section */}
        <header>
          <section 
            className="relative bg-cover bg-center bg-no-repeat h-screen flex items-center"
            style={{
              backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://public.readdy.ai/ai/img_res/da0b82d82c0623d169f9fd4da203eee3.jpg')`
            }}
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <div className="text-center text-white">
                <h1 className="text-5xl md:text-7xl font-bold mb-6">
                  {t('welcome', 'Welcome to')} <span className="text-teal-400">HKPicklePlay</span>
                </h1>
                <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
                  {t('homepage_subtitle', 'Hong Kong\'s premier pickleball community platform. Connect with players, discover courts, and elevate your game.')}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link
                    to="/courts"
                    className="bg-teal-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {t('find_courts', 'Find Courts')}
                  </Link>
                  <Link
                    to="/sessions"
                    className="bg-white text-gray-900 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {t('join_sessions', 'Join Sessions')}
                  </Link>
                </div>
              </div>
            </div>
          </section>
        </header>

        {/* Stats Section */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-4xl md:text-5xl font-bold text-teal-600 mb-2">
                    {stat.number}
                  </div>
                  <div className="text-gray-600 text-lg">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <main>
          <section className="py-20">
            <div className="container mx-auto px-6">
              <div className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                  {t('everything_you_need', 'Everything You Need for Pickleball')}
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  {t('everything_desc', 'From finding the perfect court to tracking your progress, HKPicklePlay provides all the tools you need to enjoy and improve your pickleball experience.')}
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                {features.map((feature, index) => (
                  <Link 
                    key={index}
                    to={feature.link}
                    className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow cursor-pointer group"
                  >
                    <div className="w-16 h-16 bg-teal-100 rounded-lg flex items-center justify-center mb-6 group-hover:bg-teal-200 transition-colors">
                      <i className={`${feature.icon} text-2xl text-teal-600`}></i>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600">
                      {feature.description}
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </main>

        {/* CTA Section */}
        <section className="py-20 bg-teal-600">
          <div className="container mx-auto px-6 text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {t('ready_to_start', 'Ready to Start Playing?')}
            </h2>
            <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto">
              {t('ready_desc', 'Join thousands of pickleball enthusiasts in Hong Kong. Find your next game, improve your skills, and be part of our amazing community.')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/sessions" 
                className="bg-white hover:bg-gray-100 text-teal-600 px-8 py-4 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
              >
                {t('get_started', 'Get Started')}
              </Link>
              <Link 
                to="/courts" 
                className="bg-transparent border-2 border-white hover:bg-white hover:text-teal-600 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
              >
                {t('browse_courts', 'Browse Courts')}
              </Link>
            </div>
          </div>
        </section>

        {/* Footer with quick link */}
        <footer className="bg-gray-900 text-white py-8">
          <div className="container mx-auto px-6 text-center">
            <p className="text-gray-400">
              © 2024 HK PicklePlay. All rights reserved. | 
              <a 
                href="https://readdy.ai/?origin=logo" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-teal-400 hover:text-teal-300 ml-2"
              >
                Website Builder
              </a>
            </p>
          </div>
        </footer>
      </div>
    </>
  );
}
